import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  addLineItem,
  composeTerms,
  deleteLineItem,
  getQuote,
  listClauses,
  listTermTemplates,
  newQuoteVersion,
  transitionQuote,
  type Clause,
  type LineType,
  type QuoteDetail,
  type QuoteStatus,
  type TermTemplate,
} from '@/api/quotes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

function money(v: number, currency: string, lang: string): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: currency || 'USD' }).format(v);
}

// Allowed status actions per current status (mirrors the backend lifecycle;
// VIEWED is a real interim status so ACCEPTED/DECLINED/REJECTED/EXPIRED can
// follow either SENT or VIEWED).
const ACTIONS: Record<string, QuoteStatus[]> = {
  DRAFT: ['SENT'],
  SENT: ['VIEWED', 'ACCEPTED', 'DECLINED', 'REJECTED'],
  VIEWED: ['ACCEPTED', 'DECLINED', 'REJECTED'],
};

const LINE_TYPES: LineType[] = ['PRODUCT', 'SERVICE', 'AI_AAS', 'CUSTOM'];

export function QuoteEditorPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    getQuote(id)
      .then(setQuote)
      .catch(() => setQuote(null))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  if (loading) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (!quote) return <p className="p-6 text-sm text-[var(--color-lop-danger)]">{t('quotes.notFound')}</p>;

  const cur = quote.currency;
  const lang = i18n.language;
  const canSupersede = ['DRAFT', 'SENT', 'VIEWED', 'ACCEPTED'].includes(quote.status);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs text-[var(--color-lop-text-muted)]">
            {quote.quoteNumber} · {t('quotes.versionLabel', { version: quote.version })}
          </p>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{quote.title}</h1>
          <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
            {t(`quotes.status.${quote.status}`)} · {money(quote.total, cur, lang)}
          </p>
        </div>
        <div className="flex gap-2">
          {(ACTIONS[quote.status] ?? []).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={s === 'DECLINED' || s === 'REJECTED' ? 'outline' : 'default'}
              onClick={async () => {
                let reason: string | undefined;
                if (s === 'REJECTED') {
                  reason = window.prompt(t('quotes.rejectReasonPrompt')) ?? undefined;
                }
                await transitionQuote(id, s, reason);
                load();
              }}
              data-testid={`quotes-action-${s}`}
            >
              {t(`quotes.action.${s}`)}
            </Button>
          ))}
          {canSupersede && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await newQuoteVersion(id);
                load();
              }}
              data-testid="quotes-new-version"
            >
              {t('quotes.newVersion')}
            </Button>
          )}
        </div>
      </header>

      {/* Line items */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-wide text-[var(--color-lop-text-muted)]">
          {t('quotes.lineItems')}
        </h2>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-[var(--color-lop-text-muted)]">
              <th className="py-1">{t('quotes.li.description')}</th>
              <th className="py-1">{t('quotes.li.type')}</th>
              <th className="py-1 text-right">{t('quotes.li.qty')}</th>
              <th className="py-1 text-right">{t('quotes.li.unitPrice')}</th>
              <th className="py-1 text-right">{t('quotes.li.discount')}</th>
              <th className="py-1 text-right">{t('quotes.li.lineTotal')}</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {quote.lineItems.map((li) => (
              <tr key={li.id} className="border-t border-[var(--color-lop-border)]/40">
                <td className="py-2">{li.description}</td>
                <td className="py-2 text-xs text-[var(--color-lop-text-muted)]">
                  {t(`quotes.li.lineType.${li.lineType}`)}
                  {li.mrrTotal > 0 && ` · ${t('quotes.mrr')}`}
                </td>
                <td className="py-2 text-right">{li.quantity}</td>
                <td className="py-2 text-right">{money(li.unitPrice, cur, lang)}</td>
                <td className="py-2 text-right">{li.discountPercent}%</td>
                <td className="py-2 text-right">{money(li.total, cur, lang)}</td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    className="text-[var(--color-lop-text-muted)] hover:text-[var(--color-lop-danger)]"
                    onClick={async () => {
                      await deleteLineItem(id, li.id);
                      load();
                    }}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <AddLineItemRow quoteId={id} onAdded={load} />
        <div className="mt-3 space-y-0.5 text-right text-sm">
          <div className="text-[var(--color-lop-text-secondary)]">
            {t('quotes.oneTime')}: {money(quote.subtotalOneTime, cur, lang)}
          </div>
          <div className="text-[var(--color-lop-text-secondary)]">
            {t('quotes.mrr')}: {money(quote.subtotalMrr, cur, lang)}
          </div>
          <div className="text-[var(--color-lop-text-secondary)]">
            {t('quotes.discount')}: −{money(quote.discountAmount, cur, lang)}
          </div>
          <div className="text-[var(--color-lop-text-secondary)]">
            {t('quotes.tax')}: {money(quote.taxAmount, cur, lang)}
          </div>
          <div className="font-semibold">
            {t('quotes.total')}: {money(quote.total, cur, lang)}
          </div>
        </div>
      </section>

      {/* Terms & conditions (composed from the tenant-wide clause/template catalog) */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-wide text-[var(--color-lop-text-muted)]">
          {t('quotes.terms')}
        </h2>
        {!quote.termsAndConditionsMd && <p className="text-sm text-muted-foreground">{t('quotes.noTerms')}</p>}
        {quote.termsAndConditionsMd && (
          <p className="whitespace-pre-wrap text-sm text-[var(--color-lop-text-secondary)]">
            {quote.termsAndConditionsMd}
          </p>
        )}
        <ComposeTermsRow quoteId={id} onComposed={load} />
      </section>

      {/* Activity */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-wide text-[var(--color-lop-text-muted)]">
          {t('quotes.activity')}
        </h2>
        <ul className="space-y-1 text-xs text-[var(--color-lop-text-secondary)]">
          {quote.activity.map((a, i) => (
            <li key={i}>
              {new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(a.occurredAt))} —{' '}
              {a.eventType}
              {a.actorEmail && ` (${a.actorEmail})`}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function AddLineItemRow({ quoteId, onAdded }: { quoteId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const [desc, setDesc] = useState('');
  const [lineType, setLineType] = useState<LineType>('CUSTOM');
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('0');
  const [disc, setDisc] = useState('0');
  const [recurring, setRecurring] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!desc.trim()) return;
    await addLineItem(quoteId, {
      description: desc.trim(),
      lineType,
      quantity: Number(qty) || 1,
      unitPrice: Number(price) || 0,
      discountPercent: Number(disc) || 0,
      recurring,
    });
    setDesc('');
    setQty('1');
    setPrice('0');
    setDisc('0');
    setRecurring(false);
    onAdded();
  }

  return (
    <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2">
      <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t('quotes.li.description')} className="flex-1" />
      <Select value={lineType} onChange={(e) => setLineType(e.target.value as LineType)} className="w-32">
        {LINE_TYPES.map((lt) => (
          <option key={lt} value={lt}>
            {t(`quotes.li.lineType.${lt}`)}
          </option>
        ))}
      </Select>
      <Input value={qty} onChange={(e) => setQty(e.target.value)} type="number" min="0" step="any" className="w-16" placeholder={t('quotes.li.qty')} />
      <Input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min="0" step="any" className="w-24" placeholder={t('quotes.li.unitPrice')} />
      <Input value={disc} onChange={(e) => setDisc(e.target.value)} type="number" min="0" max="100" step="any" className="w-16" placeholder="%" />
      <label className="flex items-center gap-1 text-xs text-[var(--color-lop-text-secondary)]">
        <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
        {t('quotes.li.recurring')}
      </label>
      <Button type="submit" size="sm" variant="outline" disabled={!desc.trim()} data-testid="quotes-add-line">
        <Plus className="h-4 w-4" />
      </Button>
    </form>
  );
}

function ComposeTermsRow({ quoteId, onComposed }: { quoteId: string; onComposed: () => void }) {
  const { t } = useTranslation();
  const [clauses, setClauses] = useState<Clause[]>([]);
  const [templates, setTemplates] = useState<TermTemplate[]>([]);
  const [selectedClause, setSelectedClause] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    listClauses().then(setClauses).catch(() => setClauses([]));
    listTermTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  async function compose(e: FormEvent) {
    e.preventDefault();
    if (!selectedClause && !selectedTemplate) return;
    await composeTerms(quoteId, selectedClause ? [selectedClause] : [], selectedTemplate || null);
    onComposed();
  }

  return (
    <form onSubmit={compose} className="mt-3 flex items-end gap-2">
      <Select value={selectedClause} onChange={(e) => setSelectedClause(e.target.value)} className="flex-1">
        <option value="">{t('quotes.term.selectClause')}</option>
        {clauses.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
      <Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className="flex-1">
        <option value="">{t('quotes.term.selectTemplate')}</option>
        {templates.map((tpl) => (
          <option key={tpl.id} value={tpl.id}>
            {tpl.name}
          </option>
        ))}
      </Select>
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={!selectedClause && !selectedTemplate}
        data-testid="quotes-compose-terms"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </form>
  );
}
